import React, { useContext, useEffect, useState, useMemo, useRef } from 'react';
import * as NotificationAPI from '../../services/NotificationServices';
import { getTeacherFeedback, getStudentFeedback, updateFeedback, closeFeedback as closeFeedbackAPI } from '../../services/FeedbackServices';
import { Link } from 'react-router-dom';
import { ClassroomContext } from '../../context/ClassroomContext';
import AuthContext from '../../context/AuthContext';
import styles from './styles/ClassroomFeedback.module.css';
import { FaEye, FaCheckCircle, FaPaperPlane, FaSort, FaChevronDown } from 'react-icons/fa';
// import Loading from '../loading/Loading';
import ViewSituationPreviewModal from '../scenarios/view/ViewSituationPreviewModal';

const ClassroomFeedback = () => {
    const { classroom, isTeacher, isOwner } = useContext(ClassroomContext);
    const { userId } = useContext(AuthContext);
    const [feedbacks, setFeedbacks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [newMessages, setNewMessages] = useState({});

    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
    const [selectedSituationId, setSelectedSituationId] = useState(null);

    const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);
    const [sortOption, setSortOption] = useState('date');
    const sortDropdownRef = useRef(null);

    const [expandedGroups, setExpandedGroups] = useState({});

    useEffect(() => {
        const fetchFeedback = async () => {
            if (!classroom?._id) return;

            setLoading(true);
            setError(null);

            try {
                const feedbackData = isTeacher || isOwner
                    ? await getTeacherFeedback(classroom._id)
                    : await getStudentFeedback(classroom._id);
                const sortedByDefault = feedbackData.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                setFeedbacks(sortedByDefault);
            } catch (err) {
                console.error("Error fetching feedback:", err);
                setError('Failed to load feedback.');
            } finally {
                setLoading(false);
            }
        };

        fetchFeedback();
    }, [classroom?._id, isTeacher, isOwner]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target)) {
                setIsSortDropdownOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const sortedAndGroupedFeedback = useMemo(() => {
        if (sortOption === 'date') {
            const grouped = feedbacks.reduce((acc, feedback) => {
                const date = new Date(feedback.createdAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                });
                if (!acc[date]) {
                    acc[date] = [];
                }
                acc[date].push(feedback);
                return acc;
            }, {});
            return Object.entries(grouped).sort(([dateA], [dateB]) => new Date(dateB) - new Date(dateA));
        }
        if (sortOption === 'assignment') {
            const grouped = feedbacks.reduce((acc, feedback) => {
                const assignmentTitle = feedback.assignment?.title || 'Unassigned';
                if (!acc[assignmentTitle]) {
                    acc[assignmentTitle] = {
                        feedbacks: [],
                        createdAt: feedback.assignment?.createdAt
                    };
                }
                acc[assignmentTitle].feedbacks.push(feedback);
                return acc;
            }, {});
            return Object.entries(grouped).sort(([, a], [, b]) => new Date(b.createdAt) - new Date(a.createdAt));
        }
        if (sortOption === 'student') {
            const grouped = feedbacks.reduce((acc, feedback) => {
                const studentName = feedback.sender?.username || 'Anonymous';
                if (!acc[studentName]) {
                    acc[studentName] = [];
                }
                acc[studentName].push(feedback);
                return acc;
            }, {});
            return Object.entries(grouped).sort(([studentA], [studentB]) => studentA.localeCompare(studentB));
        }
        return [];
    }, [feedbacks, sortOption]);

    useEffect(() => {
        if (sortedAndGroupedFeedback.length > 0) {
            const initialExpandedState = sortedAndGroupedFeedback.reduce((acc, [groupTitle]) => {
                acc[groupTitle] = true;
                return acc;
            }, {});
            setExpandedGroups(initialExpandedState);
        }
    }, [sortedAndGroupedFeedback]);


    const toggleGroup = (groupTitle) => {
        setExpandedGroups(prev => ({
            ...prev,
            [groupTitle]: !prev[groupTitle]
        }));
    };

    const handleSendMessage = async (feedback) => {
        const messageText = newMessages[feedback._id];
        if (!messageText || !messageText.trim()) return;

        try {
            const updatedFeedback = await updateFeedback(feedback._id, {
                message: { sender: userId, text: messageText }
            });

            setFeedbacks(prevFeedbacks =>
                prevFeedbacks.map(fb => (fb._id === feedback._id ? updatedFeedback : fb))
            );
            setNewMessages(prev => ({ ...prev, [feedback._id]: '' }));

            const notificationRecipientId = userId === feedback.sender._id
                ? feedback.recipient._id
                : feedback.sender._id;

            const notificationPayload = {
                messageSenderId: userId,
                messageText: messageText,
                classroomId: classroom?._id,
                classroomName: classroom?.name,
                assignmentTitle: feedback.assignment?.title
            };

            await NotificationAPI.sendMessageNotification(notificationRecipientId, feedback._id, notificationPayload);

        } catch (err) {
            console.error("Error sending message or notification:", err);
            alert('Failed to send message. Please try again.');
        }
    };

    const handleCloseFeedback = async (feedbackId) => {
        if (!window.confirm("Are you sure you want to close this feedback thread?")) return;

        try {
            await closeFeedbackAPI(feedbackId);

            setFeedbacks(prevFeedbacks =>
                prevFeedbacks.map(fb =>
                    fb._id === feedbackId ? { ...fb, isClosed: true } : fb
                )
            );
        } catch (err) {
            console.error("Error closing feedback:", err);
            alert('Failed to close feedback.');
        }
    };

    const handleNewMessageChange = (feedbackId, text) => {
        setNewMessages(prev => ({
            ...prev,
            [feedbackId]: text
        }));
    };

    const handleOpenPreviewModal = (situationId) => {
        setSelectedSituationId(situationId);
        setIsPreviewModalOpen(true);
    };

    const handleClosePreviewModal = () => {
        setIsPreviewModalOpen(false);
        setSelectedSituationId(null);
    };

    const formatTimestamp = (dateString) => {
        const options = {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
        };
        return new Date(dateString).toLocaleString('en-US', options);
    };

    if (error) return <div style={{ color: 'red' }}>{error}</div>;

    const renderFeedbackCard = (feedback) => (
        <div key={feedback._id} className={`${styles.feedbackCard} ${feedback.isClosed ? styles.closed : ''}`}>
             <div className={styles.cardHeader}>
                <h3 className={styles.assignmentTitle}>
                    <span>Assignment:</span> {feedback.assignment?.title || 'N/A'}
                </h3>
                <div className={styles.metaInfo}>
                    <span><strong>From:</strong> {feedback.sender?.username || 'Anonymous'}</span>
                    <span className={styles.timestamp}>
                        <strong>Submitted:</strong> {formatTimestamp(feedback.createdAt)}
                    </span>
                </div>
            </div>

            <div className={styles.initialFeedback}>
                <p>{feedback.text}</p>
                <div className={styles.originLink}>
                    <h6>Scenario: {feedback.scenarioOrigin?.title}</h6>

                    <div className={styles.situationLinkContainer}>
                        <FaEye
                            className={styles.previewIcon}
                            onClick={() => handleOpenPreviewModal(feedback.situationOrigin._id)}
                        />
                        <span className={styles.situationTitleText}>
                            Situation: {feedback.situationOrigin.title}
                        </span>
                    </div>
                </div>
            </div>

            <div className={styles.messagesContainer}>
                {feedback.messages.map((msg, index) => {
                    const isCurrentUser = msg.sender?._id === userId;
                    return (
                        <div key={index} className={`${styles.messageWrapper} ${isCurrentUser ? styles.currentUser : styles.otherUser}`}>
                            <div className={styles.message}>
                                <div className={styles.messageSender}>{msg.sender?.username || 'User'}</div>
                                <div className={styles.messageText}>{msg.text}</div>
                                <div className={styles.messageTimestamp}>{new Date(msg.createdAt).toLocaleString()}</div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {!feedback.isClosed && (
                <div className={styles.feedbackActions}>
                    <textarea
                        value={newMessages[feedback._id] || ''}
                        onChange={(e) => handleNewMessageChange(feedback._id, e.target.value)}
                        placeholder="Type your reply..."
                        className={styles.messageInput}
                    />
                    <div className={styles.actionButtons}>
                        <button className={styles.buttonPrimary} onClick={() => handleSendMessage(feedback)}>
                            <FaPaperPlane /> Send Reply
                        </button>
                        {(isTeacher || isOwner) && (
                            <button className={styles.buttonSecondary} onClick={() => handleCloseFeedback(feedback._id)}>
                                Mark as Resolved
                            </button>
                        )}
                    </div>
                </div>
            )}

            {feedback.isClosed && (
                <div className={styles.feedbackClosedNotice}>
                    <FaCheckCircle />
                    <span>This feedback thread has been resolved.</span>
                </div>
            )}
        </div>
    );

    return (
        <>
            <div className={styles.feedbackContainer}>
                <div className={styles.topBar}>
                    <h2 className={styles.title}>Classroom Feedback</h2>
                    <div className={styles.topBarActions} ref={sortDropdownRef}>
                        <button className={`${styles.button} ${styles.filterButton}`} onClick={() => setIsSortDropdownOpen(!isSortDropdownOpen)}>
                            <FaSort /> Sort
                        </button>
                        {isSortDropdownOpen && (
                            <div className={styles.sortDropdown}>
                                <div onClick={() => { setSortOption('date'); setIsSortDropdownOpen(false); }}>Sort by Date</div>
                                <div onClick={() => { setSortOption('assignment'); setIsSortDropdownOpen(false); }}>Sort by Assignment</div>
                                <div onClick={() => { setSortOption('student'); setIsSortDropdownOpen(false); }}>Sort by Student</div>
                            </div>
                        )}
                    </div>
                </div>

                {feedbacks.length === 0 && !loading ? (
                    <div className={styles.emptyState}>
                        <p>No feedback has been submitted for this classroom's assignments yet.</p>
                    </div>
                ) : (
                    <div className={styles.feedbackList}>
                        {sortedAndGroupedFeedback.map(([groupTitle, data]) => (
                            <div key={groupTitle} className={`${styles.feedbackGroup} ${expandedGroups[groupTitle] ? styles.isExpanded : ''}`}>
                                <h3 className={styles.groupTitle} onClick={() => toggleGroup(groupTitle)}>
                                    <span>{groupTitle}</span>
                                    <FaChevronDown className={`${styles.chevronIcon} ${!expandedGroups[groupTitle] ? styles.collapsedIcon : ''}`} />
                                </h3>
                                {expandedGroups[groupTitle] && (
                                    <div className={styles.feedbackListContainer}>
                                        {(Array.isArray(data) ? data : data.feedbacks).map(renderFeedbackCard)}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
            {isPreviewModalOpen && (
                <ViewSituationPreviewModal
                    situationId={selectedSituationId}
                    onClose={handleClosePreviewModal}
                    isTeacher={isTeacher}
                    isOwner={isOwner}
                />
            )}
        </>
    );
};

export default ClassroomFeedback;